import { parseStepFollow } from './moveFeats.js';
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isKey = (v) => typeof v === 'string' && /^[a-z][a-z0-9_-]{0,62}$/.test(v);
const isInt = (v, lo, hi) => typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi;
const isWords = (v, max = 120) => typeof v === 'string' && v.trim().length > 0 && v.length <= max;
const keys = (v) => (Array.isArray(v) ? v.filter(isKey) : isKey(v) ? [v] : []);
function parseRead(v) {
    if (!isObj(v))
        return null;
    if (v.read !== undefined) {
        const r = keys(v.read);
        return r.length ? { read: r, times: isInt(v.times, -10, 10) ? v.times : 1 } : null;
    }
    if (isKey(v.skill))
        return { skill: v.skill };
    if (isKey(v.ability))
        return { ability: v.ability };
    if (isInt(v.value, -1000, 1000))
        return { value: v.value, label: isWords(v.label, 40) ? v.label.trim() : String(v.value) };
    return null;
}
export const parseReads = (v) => {
    if (!Array.isArray(v) || !v.length || v.length > 8)
        return null;
    const out = v.map(parseRead);
    return out.every((x) => x !== null) ? out : null;
};
const parseAlternatives = (v) => {
    if (!Array.isArray(v) || !v.length || v.length > 4)
        return null;
    const out = v.map(parseReads);
    return out.every((x) => x !== null) ? out : null;
};
const parseEffects = (v) => {
    const o = isObj(v) ? v : {};
    return { attacker: keys(o.attacker), target: keys(o.target), clear: keys(o.clear) };
};
function parseRule(v) {
    if (!isObj(v) || !isKey(v.key) || !isWords(v.label, 40))
        return null;
    const touch = isObj(v.touch) && isKey(v.touch.defence) ? parseReads(v.touch.bonus) : null;
    const bonus = v.bonus === undefined ? undefined : parseReads(v.bonus);
    const against = v.against === undefined ? undefined : parseAlternatives(v.against);
    if (bonus === null || against === null)
        return null;
    const bonuses = Array.isArray(v.bonuses) ? v.bonuses.filter(isObj).filter((b) => isKey(b.entry) && isInt(b.value, -50, 50))
        .map((b) => ({ entry: b.entry, value: b.value, label: isWords(b.label, 40) ? b.label.trim() : b.entry.replace(/[_-]/g, ' ') })) : [];
    const fail = isObj(v.fail) ? { ...(v.fail.back === true ? { back: true } : {}), ...(isInt(v.fail.by, 1, 100) ? { by: v.fail.by } : {}), ...(keys(v.fail.attacker).length ? { attacker: keys(v.fail.attacker) } : {}) } : undefined;
    return {
        key: v.key, label: v.label.trim(), provokes: v.provokes === true, excusedBy: keys(v.excused_by), bonuses,
        ...(touch && isObj(v.touch) ? { touch: { defence: v.touch.defence, bonus: touch } } : {}),
        ...(bonus ? { bonus } : {}), ...(against ? { against } : {}), ...(isInt(v.add, -50, 50) ? { add: v.add } : {}),
        needs: keys(v.needs), targetHas: keys(v.target_has), success: parseEffects(v.success),
        ...(v.move === 'push' || v.move === 'pull' || v.move === 'through' ? { move: v.move } : {}),
        ...(fail && Object.keys(fail).length ? { fail } : {}),
        ...(isWords(v.report, 160) ? { report: v.report.trim() } : {}),
        ...(v.until_next_attack === true ? { untilNextAttack: true } : {}),
        ...(isKey(v.action) ? { action: v.action } : {}),
        ...(isObj(v.aid) && isInt(v.aid.against, 0, 100) && isInt(v.aid.value, 1, 20) && (v.aid.boost === 'attack' || v.aid.boost === 'ac') && isWords(v.aid.effect, 40) ? { aid: { against: v.aid.against, value: v.aid.value, boost: v.aid.boost, effect: v.aid.effect.trim() } } : {}),
        ...(Array.isArray(v.faster) && v.faster.length ? { faster: v.faster.filter(isObj).filter((b) => isKey(b.entry) && isKey(b.action)).map((b) => ({ entry: b.entry, action: b.action })) } : {}),
        ...(Array.isArray(v.success_with) && v.success_with.length ? { successWith: v.success_with.filter(isObj).filter((b) => isKey(b.entry) && isWords(b.report, 200)).map((b) => ({ entry: b.entry, report: b.report.trim() })) } : {}),
        ...(keys(v.lingers).length ? { lingers: keys(v.lingers) } : {}),
    };
}
/** The system's maneuvers from its data (`features.combat.maneuvers`): exact shape or null (a system with none has no maneuvers). */
export function parseManeuverRules(v) {
    if (!isObj(v) || !isObj(v.dice) || !isInt(v.dice.count, 1, 10) || !isInt(v.dice.sides, 2, 1000))
        return null;
    if (v.check !== 'opposed' && v.check !== 'defence' && v.check !== 'save')
        return null;
    const bonus = parseReads(v.bonus);
    if (!bonus)
        return null;
    const against = v.against === undefined ? [] : parseAlternatives(v.against);
    if (against === null || (v.check !== 'opposed' && !against.length))
        return null;
    const d = isObj(v.distance) ? v.distance : {};
    const s = isObj(v.scores) && isInt(v.scores.base, -100, 100) && isInt(v.scores.per, 1, 100) ? { base: v.scores.base, per: v.scores.per } : null;
    const list = Array.isArray(v.list) ? v.list.map(parseRule).filter((x) => x !== null).slice(0, 40) : [];
    if (!list.length)
        return null;
    return {
        dice: { count: v.dice.count, sides: v.dice.sides }, check: v.check, bonus, against,
        ties: v.ties === 'attacker' || v.ties === 'defender' ? v.ties : 'bonus',
        distance: { squares: isInt(d.squares, 0, 100) ? d.squares : 1, per: isInt(d.per, 1, 100) ? d.per : 5 }, scores: s, list,
    };
}
/** A side's number from its reads: the terms, or the first read that could not be answered. */
export function sideNumber(reads, readNumber) {
    const terms = [];
    for (const r of reads) {
        const got = readNumber(r);
        if (!got)
            return { missing: r };
        const times = 'times' in r ? r.times : 1;
        terms.push({ label: got.label, value: got.value * times });
    }
    return { terms };
}
/** The highest of several alternatives (Strength or Dexterity; Sense Motive or the base): the best one read, or the first that is missing. */
export function bestNumber(alts, readNumber) {
    let best = null;
    let missing = null;
    for (const a of alts) {
        const got = sideNumber(a, readNumber);
        if ('missing' in got) {
            missing ??= got.missing;
            continue;
        }
        if (!best || termsTotal(got.terms) > termsTotal(best))
            best = got.terms;
    }
    return best ? { terms: best } : { missing: missing };
}
export const termsTotal = (terms) => terms.reduce((s, t) => s + t.value, 0);
/** The words of a read the table could not answer: "Grapple", "the Bluff skill". */
export const readWords = (r) => ('read' in r ? r.read[0].replace(/[_-]/g, ' ') : 'skill' in r ? `${r.skill.replace(/[_-]/g, ' ')} skill` : 'ability' in r ? r.ability : r.label);
/**
 * A check's result: the attacker's total against the defender's (opposed: their total; a defence: its value). A tie by
 * the system's word. A move's squares by the rules' distance; a failure's try back and its cost by the maneuver's.
 */
export function maneuverOutcome(rules, m, a, d) {
    const margin = a.total - d.total;
    // A save that meets its DC succeeds, so a tie is the attacker's failure whatever `ties` says.
    const tie = rules.check === 'save' ? false : rules.ties === 'attacker' ? true : rules.ties === 'defender' ? false : a.bonus > d.bonus;
    const success = margin > 0 || (margin === 0 && tie);
    const squares = success && m.move ? rules.distance.squares + Math.floor(Math.max(0, margin) / rules.distance.per) : 0;
    const lost = success ? 0 : -margin;
    return { success, margin, squares, back: !success && m.fail?.back === true, attackerAlso: !success && m.fail?.by !== undefined && lost >= m.fail.by ? [...(m.fail.attacker ?? [])] : [] };
}
/** The maneuvers a creature may make now: those whose needed conditions it has (a grapple's round only while grappling). */
export function maneuversFor(rules, has) {
    return rules.list.filter((m) => m.needs.every(has));
}
/** Conditions after a change: `add` put on, `remove` taken off, matched as slugs, each named once. */
export function withConditions(now, add, remove) {
    const slug = (s) => s.trim().toLowerCase().replace(/[\s_]+/g, '-');
    const gone = new Set(remove.map(slug));
    const out = (now ?? []).filter((c) => !gone.has(slug(c)));
    for (const c of add)
        if (!out.some((x) => slug(x) === slug(c)))
            out.push(c);
    return out;
}
/** Where a move ends: the target `squares` along the line from the attacker through it (`push`), toward the attacker (`pull`); `through` is the ATTACKER's square, the far side of the target. */
export function movedTo(attacker, target, how, squares) {
    const di = Math.sign(target.i - attacker.i) || 0;
    const dj = Math.sign(target.j - attacker.j) || 0;
    if (how === 'through')
        return { i: target.i + di, j: target.j + dj };
    const k = how === 'push' ? squares : -Math.min(squares, Math.max(Math.abs(target.i - attacker.i), Math.abs(target.j - attacker.j)) - 1);
    return { i: target.i + di * k, j: target.j + dj * k };
}
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function parseManeuverRequest(v) {
    if (!isObj(v) || v.kind !== 'maneuver.request' || !isKey(v.maneuver) || typeof v.attacker !== 'string' || !UUID.test(v.attacker) || typeof v.target !== 'string' || !UUID.test(v.target))
        return null;
    if (Object.keys(v).some((k) => !['kind', 'maneuver', 'attacker', 'target', 'ally'].includes(k)))
        return null;
    if (v.ally !== undefined && (typeof v.ally !== 'string' || !UUID.test(v.ally)))
        return null;
    return { kind: 'maneuver.request', maneuver: v.maneuver, attacker: v.attacker, target: v.target, ...(v.ally ? { ally: v.ally } : {}) };
}
export const maneuverView = (m) => m.list.map((x) => ({ key: x.key, label: x.label, needs: [...x.needs], targetHas: [...x.targetHas], provokes: x.provokes, ...(x.aid ? { aid: true } : {}) }));
export function parseManeuverView(v) {
    if (!Array.isArray(v) || v.length > 60)
        return null;
    const out = [];
    for (const x of v) {
        if (!isObj(x) || !isKey(x.key) || !isWords(x.label, 60) || typeof x.provokes !== 'boolean')
            return null;
        out.push({ key: x.key, label: x.label.trim(), needs: keys(x.needs), targetHas: keys(x.targetHas), provokes: x.provokes, ...(x.aid === true ? { aid: true } : {}) });
    }
    return out;
}
/** The maneuvers a creature may try now, as the client lists them, by its conditions: those that need nothing, and a grapple's round actions while it is grappling. */
export function maneuversNow(list, conditions) {
    const slug = (s) => s.trim().toLowerCase().replace(/[\s_]+/g, '-');
    const has = (c) => (conditions ?? []).some((x) => slug(x) === slug(c));
    return (list ?? []).filter((m) => m.needs.every(has));
}
export function parseManeuverOffer(v) {
    if (!isObj(v) || !isWords(v.label, 60))
        return null;
    const ask = parseManeuverRequest(v.ask) ?? parseStepFollow(v.ask);
    return ask ? { label: v.label.trim(), ask } : null;
}
//# sourceMappingURL=maneuver.js.map